import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';

import { FILE_TYPE } from 'variables/constants';

import { useValidation023aResolver } from './hooks/useValidation023aResolver';
import { getHTMLId } from './resolverUtils';
import Validation006Resolver from './Validation006Resolver';
import type { ValidationResolverProps } from './types';

export interface Validation023aResolverProps extends ValidationResolverProps {
  compact?: boolean;
}

const VALIDATION_ID = '023a';
const FORM_STATE_KEY = 'autoPersonalForm';

const Validation023aResolver = ({
  section,
  itemKey: propItemKey,
  compact = false,
}: Validation023aResolverProps) => {
  const { uuid } = useParams();
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();
  const isBinderDisabled = useFormBinderDisabled(FORM_STATE_KEY);

  const itemKey = propItemKey || 'global';

  const { state, handlers, fieldIds, alertMessage, getCompactAlertStatus } =
    useValidation023aResolver({
      section,
      itemKey,
    });

  const htmlId = getHTMLId(VALIDATION_ID, itemKey);

  if (!state.show) return null;

  const isFailOnly =
    state.validationOutcome === 'fail' || state.checkType === 'a';

  if (compact) {
    return (
      <>
        <div id={htmlId}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert status={getCompactAlertStatus()} mt="10px" fontSize="14px">
            <Button onClick={onCompactOpen} w="100%" whiteSpace="normal">
              <AlertIcon />
              {state.outOfProvinceAnswer === 'yes'
                ? 'Upload file'
                : 'Add remark'}
            </Button>
          </Alert>
        </Tooltip>
        <Modal isOpen={isCompactOpen} onClose={onCompactClose} size="4xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Remarks details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb="10px">
                <Validation023aResolver
                  section={section}
                  itemKey={itemKey}
                  compact={false}
                />
              </Box>
              <Button
                variant="brand"
                onClick={onCompactClose}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box>
      <div id={htmlId}></div>
      <div id={fieldIds.outOfProvince}></div>
      <div id={fieldIds.file}></div>
      <div id={fieldIds.disclosure}></div>

      <Alert
        status={isFailOnly ? 'error' : 'warning'}
        mt="10px"
        fontSize="14px"
      >
        <AlertIcon />
        <ValidationMessage message={alertMessage} />
      </Alert>

      {isFailOnly || state.checkType === 'b' ? null : (
        <Box mt="15px" p="15px" bg="gray.50" borderRadius="md">
          <>
            <FormControl mb="15px" id={fieldIds.outOfProvince} isRequired>
              <FormLabel fontWeight="semibold">
                Was this driver previously licensed outside of Ontario, in
                another Canadian province or territory, or in the United States?
              </FormLabel>
              <RadioGroup
                value={state.outOfProvinceAnswer || ''}
                onChange={handlers.handleOutOfProvinceChange}
                isDisabled={isBinderDisabled}
              >
                <Stack direction="row" spacing={5}>
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {state.outOfProvinceAnswer === 'yes' && (
              <Box>
                <Text fontSize="sm" color="gray.700" mb="10px">
                  To ensure accurate rating for any driver with prior licensing
                  and insurance history in Canada or the USA, the following
                  documentation is required:
                </Text>
                <Text fontSize="sm" color="gray.700" mb="4px">
                  • A Letter of Experience (LOE) from each prior insurer
                  confirming full rateable insurance history
                </Text>
                <Text fontSize="sm" color="gray.700" mb="4px">
                  OR
                </Text>
                <Text fontSize="sm" color="gray.700" mb="4px">
                  • A Driver's Abstract from the prior province/state confirming
                  all quoted licensing history
                </Text>
                <Text fontSize="sm" color="gray.700" mb="10px">
                  OR
                </Text>
                <Text fontSize="sm" color="gray.700" mb="10px">
                  • If this documentation is not available, market approval is
                  required
                </Text>

                <FormControl mb="15px" id={fieldIds.resolution} isRequired>
                  <FormLabel fontWeight="semibold">
                    How would you like to resolve this?
                  </FormLabel>
                  <RadioGroup
                    value={state.resolutionMethod || ''}
                    onChange={handlers.handleResolutionMethodChange}
                    isDisabled={isBinderDisabled}
                  >
                    <Stack direction="column" spacing={2}>
                      <Radio value="loe">
                        A Letter of Experience (LOE) from each prior insurer
                        confirming full rateable insurance history
                      </Radio>
                      <Radio value="drivers_abstract">
                        A Driver's Abstract from the prior province/state
                        confirming all quoted licensing history
                      </Radio>
                      <Radio value="market_approval">
                        If this documentation is not available, market approval
                        is required
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {['loe', 'drivers_abstract'].includes(
                  state.resolutionMethod || '',
                ) &&
                  state.file && (
                    <Box>
                      <Text fontSize="sm" color="gray.600" mb="10px">
                        Upload{' '}
                        {state.resolutionMethod === 'loe'
                          ? 'a Letter of Experience.'
                          : "a Driver's Abstract."}
                      </Text>
                      <ValidateFile
                        uuid={uuid}
                        file={state.file}
                        validationId={VALIDATION_ID}
                        itemKey={itemKey}
                        formStateKey={FORM_STATE_KEY}
                      />
                    </Box>
                  )}

                {state.resolutionMethod === 'market_approval' && (
                  <Validation006Resolver
                    section={section}
                    itemKey={`${itemKey}-market`}
                    validationId={VALIDATION_ID}
                    marketApprovalFileType={
                      FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type
                    }
                    modalTitle="Date First Licensed – Out of Province Resolution"
                    forceShow
                    customAlertMessage={alertMessage}
                    hideAlertMessage
                    validationTypeKey="remarks_market_approval"
                  />
                )}
              </Box>
            )}

            {state.outOfProvinceAnswer === 'no' && (
              <Alert status="success" fontSize="sm" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text mb="3">
                    Based on the information provided, your client will be
                    prompted to sign a Licence Date Disclosure confirming the
                    accuracy of their licensing history. The signed disclosure
                    will be attached to the Remarks section of the application.
                  </Text>
                  <Text fontWeight="semibold" mb="3">
                    Client Disclosure (Driver Licensing Confirmation)
                  </Text>
                  <Text mb="3">
                    I confirm that the licensing dates shown on my application
                    represent the original date(s) I was first licensed to drive
                    in Ontario, as recorded by the Ministry of Transportation
                    (MTO).
                  </Text>
                  <Text>
                    I understand that inaccurate or incomplete disclosure of my
                    licensing history may impact rating, eligibility, or
                    coverage.
                  </Text>
                </Box>
              </Alert>
            )}
          </>
        </Box>
      )}
    </Box>
  );
};

export default Validation023aResolver;
